import React from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import {
  Truck,
  Package,
  MapPin,
  Warehouse,
  Boxes,
  ArrowRight,
  CheckCircle2,
  Navigation,
  MoveRight,
  CheckCircle
} from 'lucide-react'

import SurfaceExpressImage from '../assets/images/data/ltl-delivery-process12.png'
import CTA from './CTA'
import IndustryExpertise from './IndustryExpertise'
import MoreServicesSection from './MoreServicesSection'

const services = [
  {
    number: '01',
    icon: Truck,
    title: 'Part Truck Load (PTL)',
    description:
      'An economical solution for businesses shipping smaller consignments that do not require an entire vehicle.'
  },
  {
    number: '02',
    icon: Boxes,
    title: 'Consolidated Cargo',
    description:
      'Multiple shipments are coordinated and consolidated to optimize transportation capacity and logistics costs.'
  },
  {
    number: '03',
    icon: Warehouse,
    title: 'Pickup & Distribution',
    description:
      'Coordinated pickup, sorting, movement and distribution to support an organized cargo delivery process.'
  },
  {
    number: '04',
    icon: Navigation,
    title: 'Door-to-Door Transportation',
    description:
      'Integrated transportation support from shipment pickup to the intended delivery destination.'
  }
]

const process = [
  {
    number: '01',
    title: 'Shipment Booking',
    description:
      'Share your shipment details, pickup location and delivery requirements.'
  },
  {
    number: '02',
    title: 'Pickup & Collection',
    description:
      'Our operations team coordinates shipment pickup and collection.'
  },
  {
    number: '03',
    title: 'Sorting & Transportation',
    description:
      'Cargo is processed and moved through the appropriate transportation network.'
  },
  {
    number: '04',
    title: 'Final Delivery',
    description:
      'The shipment reaches its destination through coordinated last-mile delivery.'
  }
]

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
}

const sectionAnimation = {
  initial: 'hidden',
  whileInView: 'visible',
  viewport: { once: true, amount: 0.15 },
  variants: fadeUp,
  transition: { duration: 0.6 }
}

export default function SurfaceExpressTransportationPage () {
  return (
    <>
      <Helmet>
        <title>
          Surface Express Transportation | LTL Logistics Services | S Infinite
          Express
        </title>

        <meta
          name='description'
          content='Discover reliable and cost-effective Surface Express Transportation solutions from S Infinite Express. Our LTL logistics services support efficient cargo movement, shipment consolidation and distribution across India.'
        />

        <meta
          name='keywords'
          content='Surface Express Transportation, LTL Logistics, PTL Transportation, Part Truck Load, Surface Cargo, Cargo Transportation, S Infinite Express'
        />

        <link
          rel='canonical'
          href='https://sinfiniteexpress.com/services/surface-express'
        />
      </Helmet>

      <main className='overflow-hidden bg-white'>
        {/* HERO SECTION */}
        <section className='relative bg-gradient-to-br from-white via-blue-50/40 to-white overflow-hidden'>
          <div className='max-w-7xl mx-auto px-5 sm:px-8 lg:px-6 py-8 md:py-12'>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center'>
              {/* LEFT CONTENT */}
              <motion.div {...sectionAnimation} className='space-y-6'>
                {/* HEADING */}
                <h1 className='text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-[#211f57] leading-tight'>
                  Surface Express
                  <span className='text-[#d32c30]'> Transportation</span>
                </h1>

                <div className='w-24 h-1.5 bg-[#d32c30] rounded-full' />

                {/* DESCRIPTION */}
                <p className='text-gray-600 text-base sm:text-lg leading-relaxed'>
                  <span className='font-bold text-[#d32c30]'>
                    S INFINITE EXPRESS
                  </span>{' '}
                  delivers reliable, efficient and cost-effective surface
                  transportation solutions for businesses across India. We
                  connect destinations through dependable road freight services
                  designed to support regional and long-distance cargo movement.
                </p>

                <p className='text-gray-600 leading-relaxed'>
                  From full truckload and part truckload shipments to
                  time-sensitive deliveries, our transportation solutions focus
                  on efficient coordination, careful cargo handling and reliable
                  delivery schedules to keep your business moving.
                </p>

                {/* ACTION BUTTONS */}
                <div className='flex flex-col sm:flex-row gap-4 pt-3'>
                  <a
                    href='/get-a-quote'
                    className='inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#d32c30] text-white font-semibold rounded-xl shadow-lg hover:bg-[#b82629] hover:-translate-y-1 transition-all duration-300'
                  >
                    Get a Shipping Quote
                    <MoveRight className='w-5 h-5' />
                  </a>

                  <a
                    href='/track-shipment'
                    className='inline-flex items-center justify-center gap-3 px-7 py-4 border-2 border-[#211f57] text-[#211f57] font-semibold rounded-xl hover:bg-[#211f57] hover:text-white transition-all duration-300'
                  >
                    Track Shipment
                    <ArrowRight className='w-5 h-5' />
                  </a>
                </div>

                {/* TRUST POINTS */}
                <div className='flex flex-wrap gap-x-6 gap-y-3 pt-5 text-sm font-medium text-gray-600'>
                  <span className='flex items-center gap-2'>
                    <CheckCircle className='w-4 h-4 text-green-600 shrink-0' />
                    Reliable Transportation
                  </span>

                  <span className='flex items-center gap-2'>
                    <CheckCircle className='w-4 h-4 text-green-600 shrink-0' />
                    Nationwide Network
                  </span>

                  <span className='flex items-center gap-2'>
                    <CheckCircle className='w-4 h-4 text-green-600 shrink-0' />
                    Shipment Visibility
                  </span>
                </div>
              </motion.div>

              {/* RIGHT IMAGE */}
              <motion.div
                initial={{ opacity: 0, scale: 0.94, x: 30 }}
                whileInView={{ opacity: 1, scale: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className='relative'
              >
                {/* BACKGROUND GLOW */}
                <div className='absolute -top-6 -right-5 w-32 h-32 bg-[#fff44f]/60 rounded-full blur-3xl' />
                <div className='absolute -bottom-6 -left-5 w-32 h-32 bg-blue-200/70 rounded-full blur-3xl' />

                {/* IMAGE CARD */}
                <div className='relative p-2 bg-white rounded-3xl shadow-2xl border border-gray-100'>
                  <img
                    src={SurfaceExpressImage}
                    alt='S Infinite Express surface freight transportation truck'
                    className='w-full h-[200px] sm:h-[300px] lg:h-[420px] object-cover rounded-2xl'
                    loading='lazy'
                    decoding='async'
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* INTRODUCTION SECTION */}
        <section className='relative overflow-hidden bg-gradient-to-br from-yellow-50 via-blue-50/40 to-yellow-100/60 py-8 sm:py-10 lg:py-12'>
          {/* BACKGROUND DECORATIONS */}
          <div className='absolute -top-20 -left-20 h-64 w-64 rounded-full bg-[#fff44f]/10 blur-[90px]' />
          <div className='absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-blue-200/30 blur-[90px]' />

          <div className='relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12'>
            <div className='grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16'>
              {/* LEFT CONTENT */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className='space-y-6'
              >
                {/* HEADING */}
                <h2 className='text-2xl font-bold leading-tight text-[#211f57] sm:text-2xl lg:text-3xl'>
                  Connecting Businesses Through
                  <span className='text-[#d32c30]'>
                    {' '}
                    Smarter Transportation
                  </span>
                </h2>
                {/* DESCRIPTION */}
                <p className='text-base leading-relaxed text-gray-600 sm:text-lg'>
                  At{' '}
                  <span className='font-bold text-[#d32c30]'>
                    S INFINITE EXPRESS
                  </span>
                  , we believe efficient transportation is the foundation of a
                  stronger supply chain. Our surface logistics network helps
                  businesses manage freight movement with flexible
                  transportation options and coordinated delivery operations.
                </p>

                <p className='leading-relaxed text-gray-600'>
                  Whether moving individual consignments, consolidated shipments
                  or large commercial cargo, we focus on dependable
                  coordination, optimized transportation planning and careful
                  handling to support your business from origin to destination.
                </p>

                {/* KEY FEATURES */}
                <div className='grid grid-cols-1 gap-x-5 gap-y-4 pt-3 sm:grid-cols-2'>
                  {[
                    'Full Truckload & Part Truckload',
                    'Flexible Freight Management',
                    'Regional & Long-Haul Movement',
                    'Coordinated Delivery Operations'
                  ].map((item, index) => (
                    <div key={index} className='flex items-center gap-3'>
                      <CheckCircle2
                        size={20}
                        className='shrink-0 text-green-600'
                      />
                      <span className='text-sm font-semibold text-[#211f57]'>
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* RIGHT CONTENT - INFORMATION CARD */}
              <motion.div
                initial={{ opacity: 0, scale: 0.94, x: 30 }}
                whileInView={{ opacity: 1, scale: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className='relative'
              >
                {/* BACKGROUND GLOW */}
                <div className='absolute -right-5 -top-6 h-32 w-32 rounded-full bg-[#fff44f]/50 blur-3xl' />
                <div className='absolute -bottom-6 -left-5 h-32 w-32 rounded-full bg-blue-200/70 blur-3xl' />

                {/* MAIN CARD */}
                <div className='relative overflow-hidden rounded-3xl bg-[#211f57] p-3 shadow-2xl sm:p-4 lg:p-5'>
                  {/* DECORATIVE ELEMENTS */}
                  <div className='absolute -right-16 -top-16 h-48 w-48 rounded-full border-[25px] border-white/5' />
                  <div className='absolute -bottom-20 -right-10 h-56 w-56 rounded-full border-[25px] border-white/5' />

                  <div className='relative z-10'>
                    {/* CARD HEADER */}
                    <div className='mb-2 flex items-center gap-4'>
                      <span className='text-xs font-bold uppercase tracking-[2px] text-[#fff44f]'>
                        Our Capabilities
                      </span>
                    </div>

                    <p className='mb-2 text-sm leading-7 text-white/70 sm:text-base'>
                      Integrated road transportation solutions designed to
                      simplify freight movement, improve coordination and
                      support growing business requirements.
                    </p>

                    {/* SERVICE HIGHLIGHTS */}
                    <div className='space-y-2'>
                      {[
                        {
                          icon: Package,
                          title: 'Full Truckload (FTL)',
                          desc: 'Dedicated vehicle solutions for larger cargo shipments.'
                        },
                        {
                          icon: Boxes,
                          title: 'Part Truckload (PTL)',
                          desc: 'Flexible freight options for smaller consignments.'
                        },
                        {
                          icon: MapPin,
                          title: 'Nationwide Distribution',
                          desc: 'Connecting regional destinations through road networks.'
                        },
                        {
                          icon: Navigation,
                          title: 'Shipment Coordination',
                          desc: 'Organized cargo movement and delivery management.'
                        }
                      ].map((item, index) => {
                        return (
                          <div
                            key={index}
                            className='flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-4 transition-all duration-300 hover:bg-white/10'
                          >
                            <div>
                              <h4 className='text-sm font-bold text-white sm:text-base'>
                                {item.title}
                              </h4>
                              <p className='mt-1 text-xs leading-5 text-white/55 sm:text-sm'>
                                {item.desc}
                              </p>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section className='py-10 bg-[#f7f8fc]'>
          <div className='max-w-7xl mx-auto px-6'>
            <div className='max-w-4xl mx-auto text-center mb-5'>
              <span className='text-[#d32c30] font-extrabold text-xs uppercase tracking-[2px]'>
                What We Offer
              </span>

              <h2 className='text-3xl sm:text-4xl font-extrabold text-[#211f57] mt-2'>
                Our Surface Transportation Services
              </h2>

              <p className='text-gray-500 leading-7 mt-2'>
                Flexible logistics solutions for every shipment and business
                need.
              </p>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-6'>
              {services.map((service, index) => {
                const Icon = service.icon

                return (
                  <motion.div
                    key={service.number}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.08 }}
                    className='group bg-white p-7 rounded-2xl border border-gray-100 hover:border-[#d32c30]/30 hover:shadow-xl transition-all duration-300'
                  >
                    <div className='flex justify-between items-center'>
                      <div className='w-14 h-14 rounded-xl bg-[#211f57] group-hover:bg-[#d32c30] flex items-center justify-center text-white transition-colors'>
                        <Icon size={26} />
                      </div>

                      <span className='text-3xl font-black text-gray-100 group-hover:text-[#d32c30]/20 transition-colors'>
                        {service.number}
                      </span>
                    </div>

                    <h3 className='text-lg font-extrabold text-[#211f57] mt-3'>
                      {service.title}
                    </h3>

                    <p className='text-sm text-gray-500 leading-7 mt-2'>
                      {service.description}
                    </p>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        <IndustryExpertise />

        {/* PROCESS */}
        <section className='py-5 lg:py-6'>
          <div className='max-w-7xl mx-auto px-6'>
            <div className='text-center max-w-2xl mx-auto mb-4'>
              <span className='text-[#d32c30] font-extrabold text-xs uppercase tracking-[2px]'>
                How It Works
              </span>

              <h2 className='text-2xl sm:text-3xl font-extrabold text-[#211f57] mt-2'>
                A Simple & Coordinated Shipping Process
              </h2>

              <p className='text-gray-500 mt-2 leading-7'>
                Discover how we make shipping simple, smooth and reliable.
              </p>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-8 relative'>
              {process.map((step, index) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className='relative text-center'
                >
                  <div className='w-16 h-16 mx-auto rounded-2xl bg-[#211f57] text-white flex items-center justify-center text-xl font-extrabold shadow-lg'>
                    {step.number}
                  </div>

                  <h3 className='text-lg font-extrabold text-[#211f57] mt-6'>
                    {step.title}
                  </h3>

                  <p className='text-sm text-gray-500 leading-7 mt-3'>
                    {step.description}
                  </p>

                  {index !== process.length - 1 && (
                    <div className='hidden lg:block absolute top-8 left-[calc(50%+45px)] w-[calc(100%-50px)] border-t-2 border-dashed border-[#d32c30]/30' />
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className='relative bg-[#211f57] py-5 overflow-hidden'>
          <div className='absolute right-0 top-0 w-96 h-96 rounded-full border-[50px] border-white/5 translate-x-1/3 -translate-y-1/3' />
          <div className='relative max-w-5xl mx-auto px-6 text-center'>
            <h2 className='text-2xl sm:text-3xl lg:text-3xl font-extrabold text-white mt-2 leading-tight'>
              Looking for a Reliable
              <span className='block text-[#FFF44F]'>
                Surface Transportation Partner?
              </span>
            </h2>

            <p className='text-white/70 max-w-4xl mx-auto mt-4 leading-8'>
              Let S Infinite Express support your cargo transportation
              requirements with flexible and coordinated logistics solutions.
            </p>
          </div>
        </section>

        <MoreServicesSection />
        <CTA />
      </main>
    </>
  )
}
